import React, { useState, useRef, useEffect } from 'react';
import { InvitationDesign, ContentRow } from '../../_types/invitation-design-v2';
import { Event } from '../../_graphql/generated/operations';

// Uncontrolled contentEditable component to prevent cursor jumping
interface ContentEditableDivProps {
  initialContent: string;
  onBlur: (text: string) => void;
  onEscape: () => void;
  onInput?: () => void;
}

const ContentEditableDiv: React.FC<ContentEditableDivProps> = ({ initialContent, onBlur, onEscape, onInput }) => {
  const divRef = useRef<HTMLDivElement>(null);
  const hasInputRef = useRef(false);
  
  useEffect(() => {
    if (divRef.current) {
      // Set initial content with line breaks
      divRef.current.innerHTML = initialContent.replace(/\n/g, '<br>');
      // Focus and place cursor at the end
      divRef.current.focus();
      const range = document.createRange();
      const sel = window.getSelection();
      range.selectNodeContents(divRef.current);
      range.collapse(false);
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
  }, []);
  
  const handleInput = () => {
    if (!hasInputRef.current && onInput) {
      hasInputRef.current = true;
      onInput();
    }
  };
  
  const handleBlur = () => {
    if (divRef.current) {
      // Convert br tags back to newlines
      const html = divRef.current.innerHTML;
      const text = html
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<[^>]*>/g, '') // Remove any other HTML tags
        .replace(/&nbsp;/g, ' ') // Replace non-breaking spaces
        .replace(/&amp;/g, '&')  // Decode HTML entities
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#039;/g, "'");
      onBlur(text);
    }
    hasInputRef.current = false;
  };
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onEscape();
    }
  };
  
  return (
    <div
      ref={divRef}
      contentEditable
      suppressContentEditableWarning
      onInput={handleInput}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      style={{ outline: 'none' }}
    />
  );
};

interface InvitationPreviewV2Props {
  design: InvitationDesign;
  event: Event;
  isEditing?: boolean;
  onContentEdit?: (rowId: string, value: string) => void;
  onEditStart?: () => void;
  onRowSelect?: (rowId: string) => void;
  selectedRowId?: string | null;
  customDimensions?: {
    width?: number;
    height?: number;
  };
}

const InvitationPreviewV2: React.FC<InvitationPreviewV2Props> = ({
  design,
  event,
  isEditing = false,
  onContentEdit,
  onEditStart,
  onRowSelect,
  selectedRowId,
  customDimensions,
}) => {
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [tempContent, setTempContent] = useState<{ [key: string]: string }>({});
  
  // Clear temp content when design changes
  useEffect(() => {
    setTempContent({});
  }, [design]);

  const template = design?.template || {
    theme: {},
    contentRows: [],
    margins: { top: 40, right: 40, bottom: 40, left: 40 },
    background: { type: 'solid', value: 'white' },
    defaultTextStyle: {},
    stylePresets: {}
  };
  const theme = template.theme || {};
  
  // Process template variables in text
  const processTemplateVars = (text: string): string => {
    return text
      .replace(/\{\{template\.theme\.(\w+)\}\}/g, (match, key) => (theme as any)[key] || match)
      .replace(/\{\{event\.(\w+)\}\}/g, (match, key) => (event as any)[key] || match);
  };

  // Get computed style for a row
  const getRowStyle = (row: ContentRow): React.CSSProperties => {
    const defaultStyle = template.defaultTextStyle;
    
    // Get preset styles if available
    let presetStyle = {};
    if (row.preset && template.stylePresets && template.stylePresets[row.preset]) {
      const preset = template.stylePresets[row.preset];
      // Process template variables in preset
      presetStyle = {
        ...preset,
        fontFamily: preset.fontFamily ? processTemplateVars(preset.fontFamily) : undefined,
        color: preset.color ? processTemplateVars(preset.color) : undefined,
      };
    }
    
    const rowStyle = row.style || {};
    
    // Merge styles: default -> preset -> row overrides
    const mergedStyle = { ...defaultStyle, ...presetStyle, ...rowStyle };
    
    // Determine which font family is being used to apply correct theme modifications
    const effectiveFontFamily = mergedStyle.fontFamily ? processTemplateVars(mergedStyle.fontFamily) : theme.fontFamily;
    const isSecondaryFont = effectiveFontFamily === theme.secondaryFontFamily;
    
    // Base styles
    const style: React.CSSProperties = {
      fontSize: mergedStyle.fontSize,
      fontFamily: effectiveFontFamily,
      fontWeight: mergedStyle.fontWeight,
      fontStyle: mergedStyle.fontStyle,
      fontStretch: (mergedStyle.fontStretch || 
        (isSecondaryFont ? theme.secondaryFontStretch : theme.fontStretch) || 
        'normal') as any,
      color: mergedStyle.color ? processTemplateVars(mergedStyle.color) : theme.textColor,
      textAlign: mergedStyle.textAlign,
      lineHeight: mergedStyle.lineHeight || 1.6,
      letterSpacing: mergedStyle.letterSpacing !== undefined ? 
        `${mergedStyle.letterSpacing}px` : 
        `${isSecondaryFont ? (theme.secondaryLetterSpacing || 0) : (theme.letterSpacing || 0)}px`,
      wordSpacing: mergedStyle.wordSpacing !== undefined ? 
        `${mergedStyle.wordSpacing}px` : 
        `${isSecondaryFont ? (theme.secondaryWordSpacing || 0) : (theme.wordSpacing || 0)}px`,
      textTransform: mergedStyle.textTransform,
      whiteSpace: 'pre-wrap',
      margin: 0,
      marginTop: mergedStyle.marginTop,
      marginBottom: mergedStyle.marginBottom,
      marginLeft: mergedStyle.marginLeft,
      marginRight: mergedStyle.marginRight,
      paddingTop: mergedStyle.paddingTop,
      paddingBottom: mergedStyle.paddingBottom,
      paddingLeft: mergedStyle.paddingLeft,
      paddingRight: mergedStyle.paddingRight,
    };

    // Special handling for dividers
    if (row.type === 'divider') {
      return {
        width: rowStyle.width || 100,
        height: rowStyle.height || 1,
        backgroundColor: rowStyle.backgroundColor ? processTemplateVars(rowStyle.backgroundColor) : theme.primaryColor,
        margin: '0 auto',
        marginTop: rowStyle.marginTop,
        marginBottom: rowStyle.marginBottom,
      };
    }

    return style;
  };

  // Render border based on style
  const renderBorder = () => {
    if (!template.border || (template.border.style as string) === 'none') return null;

    const borderStyle: React.CSSProperties = {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      border: `${template.border.width}px ${template.border.style} ${template.border.color}`,
      borderRadius: template.border.radius,
      pointerEvents: 'none',
    };

    // Handle special border patterns
    if (template.border.style === 'ornamental' && template.border.pattern) {
      // Create a decorative border using CSS patterns
      const patternStyles: { [key: string]: React.CSSProperties } = {
        'classic': {
          border: `${template.border.width}px solid ${template.border.color}`,
          outline: `${template.border.width}px solid ${template.border.color}`,
          outlineOffset: `-${template.border.width * 3}px`,
        },
        'double-classic': {
          border: `${template.border.width}px double ${template.border.color}`,
          outline: `${template.border.width}px solid ${template.border.color}`,
          outlineOffset: `-${template.border.width * 4}px`,
        },
        'ornamental-classic': {
          border: `${template.border.width}px solid ${template.border.color}`,
          boxShadow: `inset 0 0 0 ${template.border.width}px ${template.border.color}, inset 0 0 0 ${template.border.width * 3}px white, inset 0 0 0 ${template.border.width * 4}px ${template.border.color}`,
        },
        'floral-vine': {
          border: `${template.border.width}px solid ${template.border.color}`,
          backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 10px, ${template.border.color}20 10px, ${template.border.color}20 20px)`,
          backgroundPosition: 'border-box',
        },
        'art-deco': {
          border: `${template.border.width}px solid ${template.border.color}`,
          clipPath: 'polygon(0 5%, 5% 0, 95% 0, 100% 5%, 100% 95%, 95% 100%, 5% 100%, 0 95%)',
        },
        'greek-key': {
          border: `${template.border.width * 2}px solid ${template.border.color}`,
          borderStyle: 'solid',
          borderImage: `repeating-linear-gradient(90deg, ${template.border.color} 0px, ${template.border.color} 10px, transparent 10px, transparent 20px, ${template.border.color} 20px, ${template.border.color} 30px) 1`,
        },
        'dots': {
          border: `${template.border.width}px dotted ${template.border.color}`,
          borderRadius: '0',
        },
        'waves': {
          border: `${template.border.width}px solid ${template.border.color}`,
          borderRadius: '20% 5% 20% 5% / 5% 20% 5% 20%',
        },
        'vintage': {
          border: `${template.border.width * 2}px solid ${template.border.color}`,
          borderStyle: 'ridge',
          boxShadow: `0 0 0 ${template.border.width}px white, 0 0 0 ${template.border.width * 2}px ${template.border.color}`,
        },
        'baroque': {
          border: `${template.border.width * 3}px solid ${template.border.color}`,
          borderStyle: 'groove',
          borderRadius: template.border.radius || 8,
        },
        'minimalist': {
          borderTop: `${template.border.width}px solid ${template.border.color}`,
          borderBottom: `${template.border.width}px solid ${template.border.color}`,
        },
        'geometric': {
          border: `${template.border.width}px solid ${template.border.color}`,
          clipPath: 'polygon(20px 0%, 100% 0%, 100% calc(100% - 20px), calc(100% - 20px) 100%, 0% 100%, 0% 20px)',
        }
      };

      const selectedPattern = patternStyles[template.border.pattern] || patternStyles['classic'];
      
      return (
        <div style={{
          ...borderStyle,
          ...selectedPattern,
        }}>
          {/* Additional decorative elements for specific patterns */}
          {['floral-corners', 'roses', 'hearts', 'wedding-bells'].includes(template.border.pattern) && (
            <>
              <div style={{
                position: 'absolute',
                top: template.border.width,
                left: template.border.width,
                width: 30,
                height: 30,
                border: `${template.border.width}px solid ${template.border.color}`,
                borderRadius: '50%',
                backgroundColor: `${template.border.color}20`,
              }} />
              <div style={{
                position: 'absolute',
                top: template.border.width,
                right: template.border.width,
                width: 30,
                height: 30,
                border: `${template.border.width}px solid ${template.border.color}`,
                borderRadius: '50%',
                backgroundColor: `${template.border.color}20`,
              }} />
              <div style={{
                position: 'absolute',
                bottom: template.border.width,
                left: template.border.width,
                width: 30,
                height: 30,
                border: `${template.border.width}px solid ${template.border.color}`,
                borderRadius: '50%',
                backgroundColor: `${template.border.color}20`,
              }} />
              <div style={{
                position: 'absolute',
                bottom: template.border.width,
                right: template.border.width,
                width: 30,
                height: 30,
                border: `${template.border.width}px solid ${template.border.color}`,
                borderRadius: '50%',
                backgroundColor: `${template.border.color}20`,
              }} />
            </>
          )}
        </div>
      );
    }

    return <div style={borderStyle} />;
  };

  // Main container style - using pixels for better responsive scaling
  const defaultWidth = 528; // 5.5 inches at 96 DPI (100% scale)
  const defaultHeight = 720; // 7.5 inches at 96 DPI (100% scale)
  
  const containerStyle: React.CSSProperties = {
    position: 'relative',
    width: '100%',
    maxWidth: customDimensions?.width ? `${customDimensions.width}px` : `${defaultWidth}px`,
    minHeight: customDimensions?.height ? `${customDimensions.height}px` : `${defaultHeight}px`,
    backgroundColor: template.background?.type === 'solid' ? template.background.value : 
                     template.background?.type === 'pattern' ? (template.background.backgroundColor || 'white') : 'white',
    backgroundImage: template.background?.type === 'gradient' ? template.background.value :
                     template.background?.type === 'image' ? `url(${template.background.value})` :
                     template.background?.type === 'pattern' ? `url(${template.background.value})` : undefined,
    backgroundSize: template.background?.type === 'pattern' ? 'auto' : 'cover',
    backgroundRepeat: template.background?.type === 'pattern' ? 'repeat' : 'no-repeat',
    backgroundPosition: 'center',
    padding: template.margins ? `${template.margins.top}px ${template.margins.right}px ${template.margins.bottom}px ${template.margins.left}px` : '40px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden',
  };

  const handleRowClick = (rowId: string) => {
    if (isEditing) {
      if (onRowSelect) {
        onRowSelect(rowId);
      }
      if (onContentEdit) {
        // Clear initialization flag from previously edited elements
        const prevEdited = document.querySelector('[data-initialized="true"]');
        if (prevEdited) {
          prevEdited.removeAttribute('data-initialized');
        }
        setEditingRowId(rowId);
        // Initialize temp content if not already set
        const row = design.content.find(r => r.id === rowId);
        if (row && row.type === 'text' && !tempContent[rowId]) {
          setTempContent(prev => ({
            ...prev,
            [rowId]: processTemplateVars(row.content || '')
          }));
        }
      }
    }
  };


  return (
    <div className="invitation-preview-content" style={containerStyle}>
      {/* Background overlay for patterns with opacity */}
      {template.background?.type === 'pattern' && template.background?.opacity && template.background.opacity < 1 && (
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url(${template.background?.value})`,
            backgroundSize: 'auto',
            backgroundRepeat: 'repeat',
            backgroundPosition: 'center',
            opacity: template.background?.opacity,
            pointerEvents: 'none',
          }}
        />
      )}
      
      {/* Background overlay for other types if needed */}
      {template.background?.type !== 'pattern' && template.background?.opacity && template.background.opacity < 1 && (
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'white',
            opacity: 1 - (template.background?.opacity || 0),
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Overlay images */}
      {design.overlays?.map((overlay) => {
        const positionStyles: React.CSSProperties = {};
        
        // Map anchor positions
        switch (overlay.anchor) {
          case 'top-left':
            positionStyles.top = overlay.offset?.y || 0;
            positionStyles.left = overlay.offset?.x || 0;
            break;
          case 'top-center':
            positionStyles.top = overlay.offset?.y || 0;
            positionStyles.left = '50%';
            positionStyles.transform = 'translateX(-50%)';
            break;
          case 'top-right':
            positionStyles.top = overlay.offset?.y || 0;
            positionStyles.right = overlay.offset?.x || 0;
            break;
          case 'center-left':
            positionStyles.top = '50%';
            positionStyles.left = overlay.offset?.x || 0;
            positionStyles.transform = 'translateY(-50%)';
            break;
          case 'center':
            positionStyles.top = '50%';
            positionStyles.left = '50%';
            positionStyles.transform = 'translate(-50%, -50%)';
            break;
          case 'center-right':
            positionStyles.top = '50%';
            positionStyles.right = overlay.offset?.x || 0;
            positionStyles.transform = 'translateY(-50%)';
            break;
          case 'bottom-left':
            positionStyles.bottom = overlay.offset?.y || 0;
            positionStyles.left = overlay.offset?.x || 0;
            break;
          case 'bottom-center':
            positionStyles.bottom = overlay.offset?.y || 0;
            positionStyles.left = '50%';
            positionStyles.transform = 'translateX(-50%)';
            break;
          case 'bottom-right':
            positionStyles.bottom = overlay.offset?.y || 0;
            positionStyles.right = overlay.offset?.x || 0;
            break;
        }

        return (
          <img
            key={overlay.id}
            src={overlay.src}
            alt=""
            style={{
              position: 'absolute',
              width: overlay.size?.width || 'auto',
              height: overlay.size?.height || 'auto',
              opacity: overlay.opacity || 1,
              transform: `${positionStyles.transform || ''} rotate(${overlay.rotation || 0}deg)`,
              pointerEvents: 'none',
              ...positionStyles,
            }}
          />
        );
      })}

      {/* Content rows */}
      {/* The content block. Its class is a MEASUREMENT HOOK as well as a name:
          the renderer's own height is the card's 720px box, but the height of
          THIS element is what decides whether a card sits or has slipped
          upwards — see src/_render/measure.ts. It was previously identifiable
          only as "the child whose inline z-index is 1", which is not something
          a later edit would know to preserve. */}
      <div className="invitation-preview-rows" style={{ position: 'relative', zIndex: 1 }}>
        {(design.content || []).map((row) => {
          if (row.type === 'text') {
            const isEditing = editingRowId === row.id;
            const rowStyle = getRowStyle(row);
            
            return (
              <div 
                key={row.id}
                data-row-id={row.id}
                data-row-type="text"
                style={{
                  ...rowStyle,
                  position: 'relative',
                }}
                onClick={() => handleRowClick(row.id)}
                className={isEditing ? 'cursor-pointer transition-all' : 'cursor-pointer hover:opacity-80'}
              >
                {isEditing ? (
                  <ContentEditableDiv
                    key={`${row.id}-edit`}
                    initialContent={tempContent[row.id] || processTemplateVars(row.content || '')}
                    onInput={onEditStart}
                    onBlur={(text) => {
                      if (onContentEdit) {
                        onContentEdit(row.id, text);
                        // Clear temp content after saving
                        setTempContent(prev => {
                          const newTemp = { ...prev };
                          delete newTemp[row.id];
                          return newTemp;
                        });
                      }
                      setEditingRowId(null);
                    }}
                    onEscape={() => {
                      setEditingRowId(null);
                      // Clear temp content without saving
                      setTempContent(prev => {
                        const newTemp = { ...prev };
                        delete newTemp[row.id];
                        return newTemp;
                      });
                    }}
                  />
                ) : (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: processTemplateVars(row.content || '').replace(/\n/g, '<br>')
                    }}
                    style={{
                      outline: 'none',
                    }}
                  />
                )}
              </div>
            );
          }

          if (row.type === 'divider') {
            return (
              <div 
                key={row.id}
                data-row-id={row.id}
                data-row-type="divider"
                style={{
                  ...getRowStyle(row),
                  position: 'relative',
                }}
                onClick={() => isEditing && onRowSelect && onRowSelect(row.id)}
                className={isEditing ? 'cursor-pointer' : ''}
              />
            );
          }

          if (row.type === 'image' && row.src) {
            const rowStyle = getRowStyle(row);
            return (
              <div
                key={row.id}
                data-row-id={row.id}
                data-row-type="image"
                style={{ textAlign: rowStyle.textAlign || 'center', ...rowStyle }}
              >
                <img
                  src={row.src}
                  alt={row.alt || ''}
                  style={{
                    maxWidth: '100%',
                    width: row.style?.width || 'auto',
                    height: row.style?.height || 'auto',
                  }}
                />
              </div>
            );
          }

          return null;
        })}
      </div>

      {/* Border */}
      {renderBorder()}
    </div>
  );
};

export default InvitationPreviewV2;